/*
  Shared "Recently viewed" engine for Hey Jack.

  History lives in localStorage under hj_recently_viewed as an array of product
  handles, oldest first, capped at 10. The product page records the current
  handle on each visit (registerHandle); both the product page and the homepage
  read that history and render a carousel of cards (init).

  Storing only handles keeps the record tiny and always current: init fetches
  each product fresh from the Shopify product JSON endpoint, so prices and
  images never go stale. Card markup mirrors snippets/product-card.liquid so the
  rendered cards match the rest of the site exactly. All localStorage access is
  wrapped in try/catch because private browsing can throw on read or write.

  Exposes window.HeyJackRecentlyViewed with init(root, options),
  registerHandle(handle), and list() (the raw handle history, oldest first, for
  other surfaces such as the homepage personalized picks).
*/
(function () {
  var STORAGE_KEY = 'hj_recently_viewed';
  var MAX = 10;

  function readHandles() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) {
        return [];
      }
      var parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  function writeHandles(handles) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(handles));
    } catch (e) {}
  }

  // Append a handle to the history, removing any earlier occurrence first so
  // the most recent visit moves to the end. Keep at most MAX entries.
  function registerHandle(handle) {
    if (!handle) {
      return;
    }
    var handles = readHandles().filter(function (h) {
      return h && h !== handle;
    });
    handles.push(handle);
    if (handles.length > MAX) {
      handles = handles.slice(handles.length - MAX);
    }
    writeHandles(handles);
  }

  // Local money formatter. The product JSON endpoint returns prices in cents.
  function formatMoney(cents) {
    var amount = (Number(cents) || 0) / 100;
    return '$' + amount.toFixed(2);
  }

  // Request a sized variant of a Shopify CDN image by adding a width param.
  function sizeImage(url, width) {
    if (!url) {
      return '';
    }
    var sep = url.indexOf('?') === -1 ? '?' : '&';
    return url + sep + 'width=' + width;
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // Build one card element from a product JSON object. Mirrors the structure of
  // snippets/product-card.liquid: panel with a contained featured image at rest
  // and a covered second image on hover, a sale badge when on sale, an inert
  // heart, title, and formatted price.
  function buildCard(product) {
    var handle = product.handle || '';
    var title = product.title || '';
    var price = product.price;
    var compareAt = product.compare_at_price;
    var onSale = compareAt && compareAt > price;
    var featured = product.featured_image;
    var images = product.images || [];
    var second = images.length > 1 ? images[1] : null;

    var html = '';
    html += '<a class="hj-pcard" href="/products/' + escapeHtml(handle) + '">';
    html += '<div class="hj-pcard__panel">';
    html += '<div class="hj-pcard__badges">';
    if (onSale) {
      html += '<span class="hj-badge hj-badge--sale">On Sale</span>';
    }
    html += '</div>';
    html += '<button class="hj-pcard__fav" type="button" aria-label="Save to favorites" data-favorite-toggle data-handle="' + escapeHtml(handle) + '">';
    html += '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8Z"></path></svg>';
    html += '</button>';
    if (featured) {
      // width/height match the 2:3 .hj-pcard__panel. The image is absolutely
      // positioned and fills the panel via object-fit, so the panel's CSS
      // aspect-ratio controls layout; these attributes give the browser an
      // up-front aspect hint and satisfy the "explicit width and height" audit.
      html += '<img class="hj-pcard__img hj-pcard__img--base" src="' + escapeHtml(sizeImage(featured, 600)) + '" alt="' + escapeHtml(title) + '" width="600" height="900" loading="lazy" />';
      if (second) {
        html += '<img class="hj-pcard__img hj-pcard__img--hover" src="' + escapeHtml(sizeImage(second, 600)) + '" alt="' + escapeHtml(title) + '" width="600" height="900" loading="lazy" />';
      }
    } else {
      html += '<div class="hj-pcard__art"></div>';
    }
    html += '</div>';
    html += '<div class="hj-pcard__info">';
    html += '<h3 class="hj-pcard__title">' + escapeHtml(title) + '</h3>';
    html += '<p class="hj-pcard__price">';
    if (onSale) {
      html += '<span class="hj-pcard__price-was">' + formatMoney(compareAt) + '</span>';
      html += '<span class="hj-pcard__price-now">' + formatMoney(price) + '</span>';
    } else {
      html += '<span class="hj-pcard__price-now">' + formatMoney(price) + '</span>';
    }
    html += '</p>';
    html += '</div>';
    html += '</a>';

    var tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.firstElementChild;
  }

  // Fill the carousel inside root and reveal it. options.excludeHandle keeps the
  // current product (on a product page) out of its own "recently viewed" list;
  // the homepage passes no excludeHandle.
  function init(root, options) {
    if (!root) {
      return;
    }
    options = options || {};
    var excludeHandle = options.excludeHandle || '';

    var list = root.querySelector('[data-recently-viewed-list]');
    if (!list) {
      return;
    }
    // The list element is an empty marker placed directly in the carousel track.
    // Cards are inserted as siblings before it so each becomes a track item and
    // picks up the shared carousel sizing; the marker is then removed.
    var track = list.parentNode;
    if (!track) {
      return;
    }

    // History is oldest first; show most recent first and drop the excluded one.
    var handles = readHandles().slice().reverse().filter(function (h) {
      return h && h !== excludeHandle;
    }).slice(0, MAX);

    if (handles.length === 0) {
      if (list.parentNode) {
        list.parentNode.removeChild(list);
      }
      return;
    }

    var fetches = handles.map(function (handle) {
      return fetch('/products/' + encodeURIComponent(handle) + '.js', { credentials: 'same-origin' })
        .then(function (res) {
          return res.ok ? res.json() : null;
        })
        .catch(function () {
          return null;
        });
    });

    Promise.all(fetches).then(function (products) {
      var shown = 0;
      products.forEach(function (product) {
        if (!product || !product.handle) {
          return;
        }
        track.insertBefore(buildCard(product), list);
        shown += 1;
      });
      if (list.parentNode) {
        list.parentNode.removeChild(list);
      }
      if (shown > 0) {
        root.style.display = '';
      }
    });
  }

  // The raw handle history, oldest first (the storage order). Surfaces that
  // build their own UI (e.g. the homepage recs band) read this directly.
  function list() {
    return readHandles();
  }

  window.HeyJackRecentlyViewed = {
    init: init,
    registerHandle: registerHandle,
    list: list
  };
})();
